import { useEffect, useState } from 'react'
import { Field } from '@/components/base/fieldset'
import { Heading } from '@/components/base/heading'
import { Listbox, ListboxLabel, ListboxOption } from '@/components/base/listbox'
import { Stat } from './stat'
import { useQuery } from '@apollo/client'
import { OverviewQuery, OverviewQueryVariables } from './__generated__/overview_query'
import { OverviewQuery as Query } from './overview_query'
import { Inline as Error } from '@/components/error'
import { addMonths, addWeeks, addYears, parseISO, subMonths, subWeeks, subYears } from 'date-fns'

export const Overview = () => {
  const timespans = ['week', 'month', 'quarter', 'year']
  const [timespan, setTimespan] = useState('week')
  const [variables, setVariables] = useState<Partial<OverviewQueryVariables>>({})
  const [eventCount, setEventCount] = useState({ current: 0, change: 0 })
  const [crewCount, setCrewCount] = useState({ current: 0, change: 0 })

  useEffect(() => {
    const now = new Date
    switch (timespan) {
      case 'week':
        setVariables({
          eventFilters: { dateReturnGteq: subWeeks(now, 1), dateOutLteq: addWeeks(now, 1) }
        })
        break
      case 'month':
        setVariables({
          eventFilters: { dateReturnGteq: subMonths(now, 1), dateOutLteq: addMonths(now, 1) }
        })
        break
      case 'quarter':
        setVariables({
          eventFilters: { dateReturnGteq: subMonths(now, 3), dateOutLteq: addMonths(now, 3) }
        })
        break
      case 'year':
        setVariables({
          eventFilters: { dateReturnGteq: subYears(now, 1), dateOutLteq: addYears(now, 1) }
        })
        break
    }
  }, [timespan])

  const { data, loading, error } = useQuery<OverviewQuery>(Query, { variables })

  useEffect(() => {
    if (!data) return

    const now = new Date
    const current = data.events.nodes.filter(event => parseISO(event.dateOut) >= now).length
    const previous = data.events.nodes.length - current

    setEventCount({ current, change: current - previous })
  }, [data, timespan, setEventCount])

  useEffect(() => {
    if (!data) return

    let cutoff = new Date
    switch (timespan) {
      case 'week': cutoff = subWeeks(cutoff, 1); break
      case 'month': cutoff = subMonths(cutoff, 1); break
      case 'quarter': cutoff = subMonths(cutoff, 3); break
      case 'year': cutoff = subYears(cutoff, 1); break
    }

    const current = data.companyUsers.nodes.length
    const previous = data.companyUsers.nodes.filter(user => user.acceptedAt && parseISO(user.acceptedAt) <= cutoff).length

    setCrewCount({ current, change: current - previous })
  }, [data, timespan, setCrewCount])

  if (error) return <Error message={error.message} />

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex flex-row justify-between">
          <Heading>Overview</Heading>
          <Field>
            <Listbox defaultValue="week" name="timespan" value={timespan} onChange={setTimespan}>
              { timespans.map(timespan => (
                <ListboxOption key={timespan} value={timespan}>
                  <ListboxLabel className="capitalize">{ timespan }</ListboxLabel>
                </ListboxOption>
              )) }
            </Listbox>
          </Field>
        </div>
        <div className="grid grid-cols-4 gap-8">
          <Stat
            key="upcoming_events"
            change={eventCount.change}
            loading={loading}
            timespan={timespan}
            title="Upcoming events"
            value={eventCount.current}
          />
          <Stat
            key="event_profit"
            change={0}
            loading={loading}
            timespan={timespan}
            title="Event profit"
            value="£0"
          />
          <Stat
            key="crew_pool"
            change={crewCount.change}
            loading={loading}
            timespan={timespan}
            title="Crew pool"
            value={crewCount.current}
          />
          <Stat
            key="tracked_equipment"
            change={0}
            loading={loading}
            timespan={timespan}
            title="Tracked equipment"
            value="0"
          />
        </div>
      </div>
    </>
  )
}
